import { type Command, TextSelection } from 'prosemirror-state'

import { isLockedPageBreak } from '#util/pagination/helpers'

const NOT_ALLOWED_MSG =
  'Operation not allowed! Selection contains locked pages.'

/**
 * detect if selection runs across one or more locked page boundaries
 * SIDE EFFECT: alerts user of bad operation
 **/
export const lockedPagesInSelection: Command = (state) => {
  const { $from, $to } = state.selection
  if ($from.parent.eq($to.parent)) return false

  let foundCount = 0
  state.doc.nodesBetween($from.pos, $to.pos, (node) => {
    if (isLockedPageBreak(node)) foundCount++
    // no need to keep digging
    if (node.isTextblock) return false
  })

  if (foundCount === 0) return false

  // two locked page boundaries signifies a selection that cuts across locked pages
  // as does one locked page boundary not positioned at the absolute start of the selection
  if (foundCount > 1 || !isLockedPageBreak($from.node(2))) {
    window.alert(NOT_ALLOWED_MSG)
    return true
  }

  return false
}

/**
 * - if selection includes locked page boundary, prevent transaction
 * - if needed, move cursor to previous page instead of collapsing
 * - TODO: if page is empty, consider deleting the whole page
 */
export const lockedPageBackspace: Command = (state, dispatch) => {
  // prevent if there's an active selection containing locked pages
  if (!state.selection.empty) return lockedPagesInSelection(state)

  // if we aren't at the start of a block, short-circuit
  const { $from } = state.selection
  if ($from.parentOffset > 0) return false

  // if we *are* at a locked page boundary, shift cursor to the prev page instead
  if (!isLockedPageBreak($from.parent)) return false

  const { tr } = state
  const $before = tr.doc.resolve($from.pos - 2)
  const sel = TextSelection.findFrom($before, -1, true)
  if (sel) tr.setSelection(sel)
  dispatch?.(tr.scrollIntoView())

  // TODO: we *could* detect completely empty locked pages,
  // allow deletion and rollup the two page numbers programmatically
  // but this would give access to *any* writer to manage locked pages
  return true
}

/**
 * if selection includes locked page boundary, prevent transaction
 * if needed, shift cursor to the next page instead of collapsing
 */
export const lockedPageDelete: Command = (state, dispatch) => {
  // prevent if there's an active selection containing locked pages
  if (!state.selection.empty) return lockedPagesInSelection(state)

  const { $from } = state.selection
  // skip if we're not at the END of a text block
  const atEnd = $from.parentOffset === $from.node().content.size
  if (!atEnd) return false

  const $next = state.doc.resolve($from.pos + 2)
  if (!isLockedPageBreak($next.parent)) return false

  const { tr } = state
  const sel = TextSelection.findFrom($next, 1, true)
  if (sel) tr.setSelection(sel)
  dispatch?.(tr.scrollIntoView())
  return true
}

/**
 * disable when selection spans a locked page boundary
 */
export const lockedPageEnter: Command = (state) => {
  if (state.selection.empty) return false
  return lockedPagesInSelection(state)
}
